/**
 * @file CustomFontUploadPanel.tsx
 * @description Второй экран окна каталога — «← К каталогу · Свой шрифт» или
 * «Добавить начертание — Marianna» (phase19.16 Р1-7; SB99, SB103; проект
 * uiux §3–§4). Не отдельное окно: человек остаётся в выборе шрифта роли.
 *
 * Название (подставляется из файла), вид шрифта, четыре места начертаний,
 * источник/лицензия, галочка права. В подвале — «Осталось: …» только
 * недостающее и [Отмена] [Загрузить]. Сбой связи — полоса с [Повторить],
 * файлы остаются в окне. При догрузке название и занятые места — только
 * для чтения. На 360 окно на весь экран, кнопки прижаты к низу; заголовок
 * догрузки переносится перед названием шрифта, название — целиком.
 *
 * При открытии фокус на первом доступном поле («Название» или «Выбрать
 * файл»), на телефоне (<md, `PHONE_QUERY`) — на заголовке экрана; о
 * выбранных файлах экран сообщает окну (`onDirtyChange`) — тогда
 * Escape возвращает к каталогу, а не закрывает окно.
 */

import type { SiteFontCategory } from "@chaiberi/shared";
import { ArrowLeft } from "lucide-react";
import { useEffect, useRef } from "react";

import { Button } from "../../ui/button";
import { DialogTitle } from "../../ui/dialog";
import { Label } from "../../ui/label";
import { NoticeStrip } from "../../ui/NoticeStrip";
import { Segmented } from "../Segmented";
import { TextField } from "../TextField";
import { ConsentRow } from "./ConsentRow";
import { FontWeightSlot } from "./FontWeightSlot";
import { PHONE_QUERY } from "../../../hooks/useMediaQuery";
import { useFontUploadForm, type FontUploadForm } from "../../../hooks/site-view/useFontUploadForm";
import { FONT_NETWORK_TEXT } from "../../../lib/site-view/shop-font-errors";
import {
  ADD_FILE_NOTE,
  FONT_SLOTS,
  SHOP_FONT_CATEGORY_OPTIONS,
  slotMeta,
  slotOf,
  type FontSlotKey,
  type FontSlotMeta,
  type ShopFontDto,
} from "../../../lib/site-view/shop-fonts";

interface Props {
  /** Шрифт для догрузки начертания; `null` — новый шрифт. */
  font: ShopFontDto | null;
  defaultCategory: SiteFontCategory;
  /** Выбраны ли файлы (Escape в окне — назад к каталогу). */
  onDirtyChange?: (dirty: boolean) => void;
  onBack: () => void;
  onDone: (font: ShopFontDto) => void;
}

/** Куда переложить файл, если проверка считает его другим начертанием. */
function suggestFor(form: FontUploadForm, key: FontSlotKey): FontSlotMeta | null {
  const slot = form.slots[key];
  if (slot.status !== "ready" || slot.info.variable) return null;
  const guess = slotOf(slot.info.weightGuess, slot.info.styleGuess);
  if (guess === key || form.loaded.includes(guess) || form.slots[guess].status !== "empty") return null;
  return slotMeta(guess);
}

export function CustomFontUploadPanel({ font, defaultCategory, onDirtyChange, onBack, onDone }: Props) {
  const form = useFontUploadForm({ font, defaultCategory, onDone });
  const root = useRef<HTMLDivElement | null>(null);
  const { hasFiles } = form;

  useEffect(() => {
    // На телефоне фокус в поле открыл бы клавиатуру и закрыл места для файлов — фокус на заголовок.
    const phone = typeof window.matchMedia === "function" && window.matchMedia(PHONE_QUERY).matches;
    const target = phone ? "[data-panel-title]" : 'input:not([disabled]):not([type="file"]), button[data-choose-file]:not([disabled])';
    root.current?.querySelector<HTMLElement>(target)?.focus();
  }, []);
  useEffect(() => onDirtyChange?.(hasFiles), [hasFiles, onDirtyChange]);

  return (
    <div ref={root} className="flex min-h-0 flex-1 flex-col gap-4">
      <div className="flex shrink-0 flex-wrap items-center gap-x-2 gap-y-1 pr-10">
        <Button variant="ghost" onClick={onBack}>
          <ArrowLeft aria-hidden />К каталогу
        </Button>
        <span aria-hidden className="text-on-surface-variant max-sm:hidden">
          ·
        </span>
        <DialogTitle tabIndex={-1} data-panel-title="" className="min-w-0 text-balance outline-none max-sm:basis-full">
          {font ? (
            <>
              Добавить начертание — <span className="inline-block max-w-full break-words">{font.family}</span>
            </>
          ) : (
            "Свой шрифт"
          )}
        </DialogTitle>
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto pr-1">
        <TextField
          label="Название шрифта *"
          value={form.family}
          onChange={form.setFamily}
          max={60}
          disabled={!!font}
          hint={font ? undefined : "Так шрифт будет называться в списке. Подставили из файла — можно изменить."}
          error={form.familyError}
        />
        {font ? null : (
          <div className="flex flex-col gap-2">
            <Label>Вид шрифта</Label>
            <Segmented aria-label="Вид шрифта" options={SHOP_FONT_CATEGORY_OPTIONS} value={form.category} onChange={form.setCategory} />
          </div>
        )}
        <div className="flex flex-col gap-1">
          <p className="type-caption text-on-surface-variant">Начертания — нужен хотя бы обычный. Файлы .woff2, .ttf, .otf до 2 МБ</p>
          {font ? <p className="type-caption text-on-surface-variant">{ADD_FILE_NOTE}</p> : null}
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          {FONT_SLOTS.map((meta) => (
            <FontWeightSlot
              key={meta.key}
              meta={font ? { ...meta, required: false } : meta}
              state={form.slots[meta.key]}
              loaded={form.loaded.includes(meta.key)}
              suggest={suggestFor(form, meta.key)}
              disabled={form.submitting}
              onPick={(file) => void form.pick(meta.key, file)}
              onClear={() => form.clear(meta.key)}
              onMove={(to) => form.move(meta.key, to.key)}
              onKeep={() => form.keep(meta.key)}
            />
          ))}
        </div>
        {font ? null : (
          <TextField
            label="Откуда шрифт, лицензия (необязательно)"
            value={form.sourceNote}
            onChange={form.setSourceNote}
            max={300}
            multiline
          />
        )}
        <ConsentRow checked={form.consent} onChange={form.setConsent} disabled={form.submitting} />
        {form.networkError ? (
          <NoticeStrip
            tone="error"
            action={
              <Button variant="outline" onClick={() => void form.submit()}>
                Повторить
              </Button>
            }
          >
            {FONT_NETWORK_TEXT}
          </NoticeStrip>
        ) : null}
      </div>

      <div className="flex shrink-0 flex-col gap-2 border-t border-[var(--border)] pt-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="type-caption text-on-surface-variant" aria-live="polite">
          {form.missing.length > 0 ? `Осталось: ${form.missing.join(" · ")}` : ""}
        </p>
        <div className="flex flex-col-reverse gap-2 sm:flex-row">
          <Button variant="ghost" onClick={onBack}>
            Отмена
          </Button>
          <Button disabled={!form.canSubmit} loading={form.submitting} onClick={() => void form.submit()}>
            Загрузить
          </Button>
        </div>
      </div>
    </div>
  );
}
